import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faCalendar, faClock, faDownload, faLocationDot } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { formatEventDate, safeString } from "../utils/calendarInviteData.helper";

interface CalendarInviteCardProps {
	conversation: Conversation;
}

const STATUS_LABELS: Record<string, string> = {
	PENDING: "calendarInvite.status.pending",
	ACCEPTED: "calendarInvite.status.accepted",
	DECLINED: "calendarInvite.status.declined",
	TENTATIVE: "calendarInvite.status.tentative",
};

const STATUS_COLORS: Record<string, string> = {
	PENDING: "bg-status-warning/10 text-status-warning",
	ACCEPTED: "bg-status-success/10 text-status-success",
	DECLINED: "bg-status-error/10 text-status-error",
	TENTATIVE: "bg-blue-action/10 text-blue-action",
};

const generateIcsFile = (data: NonNullable<Conversation["calendarInviteData"]>) => {
	try {
		const icsContent = [
			"BEGIN:VCALENDAR",
			"VERSION:2.0",
			"PRODID:-//Signalist//Calendar//EN",
			"BEGIN:VEVENT",
			`UID:${safeString(data.uid)}`,
			`SUMMARY:${safeString(data.eventSummary)}`,
			`DTSTART:${safeString(data.eventStart)}`,
			`DTEND:${safeString(data.eventEnd)}`,
			data.eventLocation ? `LOCATION:${safeString(data.eventLocation)}` : "",
			data.eventDescription ? `DESCRIPTION:${safeString(data.eventDescription)}` : "",
			data.organizer ? `ORGANIZER:${safeString(data.organizer)}` : "",
			`SEQUENCE:${data.sequence ?? 0}`,
			"END:VEVENT",
			"END:VCALENDAR",
		]
			.filter(Boolean)
			.join("\r\n");

		const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = url;
		link.download = `${safeString(data.eventSummary) || "event"}.ics`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Failed to generate ICS file:", error);
	}
};

export const CalendarInviteCard: FunctionComponent<CalendarInviteCardProps> = ({
	conversation,
}) => {
	const { t } = useTranslation();

	const data = conversation.calendarInviteData;

	if (!data) {
		return null;
	}

	const rawStatus = safeString(data.status);
	const status = rawStatus && rawStatus in STATUS_LABELS ? rawStatus : "PENDING";

	return (
		<div className="border border-border-light rounded-lg bg-brand-white p-4 my-2 max-w-md">
			<div className="flex items-center gap-2 mb-3">
				<Icon className="text-blue-action w-4 h-4" icon={faCalendar} />
				<span className="text-sm font-semibold text-text">{t("calendarInvite.title")}</span>
				<span
					className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[status] ?? STATUS_COLORS.PENDING}`}
				>
					{t(STATUS_LABELS[status] ?? STATUS_LABELS.PENDING)}
				</span>
			</div>

			<div className="flex flex-col gap-1.5 mb-3">
				<div className="text-sm font-semibold text-text">{safeString(data.eventSummary)}</div>
				<div className="flex items-center gap-1.5 text-xs text-text-meta">
					<Icon className="w-3 h-3" icon={faClock} />
					<span>
						{formatEventDate(data.eventStart)} — {formatEventDate(data.eventEnd)}
					</span>
				</div>
				{data.eventLocation && (
					<div className="flex items-center gap-1.5 text-xs text-text-meta">
						<Icon className="w-3 h-3" icon={faLocationDot} />
						<span>{safeString(data.eventLocation)}</span>
					</div>
				)}
			</div>

			<div className="flex items-center gap-2 flex-wrap">
				<div className="w-fit">
					<Tooltip content={t("calendarInvite.addToCalendarTooltip")}>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-1.5 w-3 h-3" icon={faDownload} />}
							size={ButtonSize.XS}
							title={t("calendarInvite.addToCalendar")}
							onClick={() => generateIcsFile(data)}
						/>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};
